import type { BookingContextParams, HoneymoonBookingEntryPoint, TripBookingOrigin } from '@shared/constants/bookingContext.types';

/**
 * What a booking is for and which Joy surface it started on (TRAV-5008), carried on the URL from that
 * surface to the reservation create, which stores them for the warehouse.
 *
 * Their own params rather than UTMs: an inbound acquisition campaign overwrites `utm_campaign` on every
 * internal hop (`resolveAcquisitionAwareUtm`), and the hotel page opens in a new tab outside the
 * surface, so nothing but the URL survives the trip. Only the surfaces that mean it set them; a URL
 * without them books exactly as before.
 */
export const BOOKING_PURPOSE_PARAM = 'bookingPurpose';
export const BOOKING_ENTRY_POINT_PARAM = 'bookingEntryPoint';

/**
 * The trip board, and the save on it, a booking was started from (TRAV-5279). They ride the same hops
 * as the purpose, so the checkout can record its reservation on that board; the reservation create
 * never sees them.
 */
export const TRIP_BOARD_PARAM = 'tripBoardId';
export const TRIP_SAVE_PARAM = 'tripSaveId';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The one purpose a surface sets today. */
export const HONEYMOON_BOOKING_PURPOSE = 'honeymoon';

// A Record, not a list: a member missing from either this or the union fails to compile.
const HONEYMOON_BOOKING_ENTRY_POINTS: Readonly<Record<HoneymoonBookingEntryPoint, true>> = {
  honeymoon_explore: true,
  honeymoon_board: true,
};

const isHoneymoonBookingEntryPoint = (value: string): value is HoneymoonBookingEntryPoint => {
  return Object.prototype.hasOwnProperty.call(HONEYMOON_BOOKING_ENTRY_POINTS, value);
};

/** The params a honeymoon planner link adds, so its booking is recorded as a honeymoon (and on its trip). */
export const honeymoonBookingContextParams = (entryPoint: HoneymoonBookingEntryPoint, trip?: TripBookingOrigin | null): Readonly<Record<string, string>> => {
  return {
    [BOOKING_PURPOSE_PARAM]: HONEYMOON_BOOKING_PURPOSE,
    [BOOKING_ENTRY_POINT_PARAM]: entryPoint,
    ...(trip && { [TRIP_BOARD_PARAM]: trip.boardId }),
    ...(trip?.tripSaveId && { [TRIP_SAVE_PARAM]: trip.tripSaveId }),
  };
};

/** Adds the honeymoon booking context to a link's query. */
export const setHoneymoonBookingContext = (query: URLSearchParams, entryPoint: HoneymoonBookingEntryPoint, trip?: TripBookingOrigin | null): void => {
  for (const [param, value] of Object.entries(honeymoonBookingContextParams(entryPoint, trip))) {
    query.set(param, value);
  }
};

/**
 * The booking context a URL may carry forward. The params arrive from a link anyone can edit, so only
 * known values pass: anything else is dropped here, before it reaches the next URL or a create (whose
 * input validation would otherwise refuse an oversized value and fail the checkout). The entry point
 * and the trip travel only with a purpose, and a save only with its board.
 */
export const toBookingContextParams = (
  purpose: string | undefined,
  entryPoint: string | undefined,
  trip: Readonly<{ boardId?: string; tripSaveId?: string }> = {},
): BookingContextParams => {
  if (purpose !== HONEYMOON_BOOKING_PURPOSE) {
    return {};
  }
  const boardId = trip.boardId && UUID_PATTERN.test(trip.boardId) ? trip.boardId : undefined;
  const tripSaveId = boardId && trip.tripSaveId && UUID_PATTERN.test(trip.tripSaveId) ? trip.tripSaveId : undefined;
  return {
    [BOOKING_PURPOSE_PARAM]: purpose,
    ...(entryPoint && isHoneymoonBookingEntryPoint(entryPoint) && { [BOOKING_ENTRY_POINT_PARAM]: entryPoint }),
    ...(boardId && { [TRIP_BOARD_PARAM]: boardId }),
    ...(tripSaveId && { [TRIP_SAVE_PARAM]: tripSaveId }),
  };
};

/** The trip a URL's booking context names, or null when it names none. */
export const toTripBookingOrigin = (params: BookingContextParams): TripBookingOrigin | null => {
  const boardId = params[TRIP_BOARD_PARAM];
  return boardId ? { boardId, tripSaveId: params[TRIP_SAVE_PARAM] ?? null } : null;
};
